export interface D0RuntimeMetrics {
  readonly firstPaintMs: number | null;
  readonly lastSelectionMs: number | null;
  readonly worstSelectionMs: number | null;
  readonly selectionCount: number;
  readonly heapBytes: number | null;
}

type Clock = () => number;
type HeapReader = () => number | null;

interface PerformanceMemory {
  readonly usedJSHeapSize?: number;
}

function readBrowserHeap(): number | null {
  const performanceWithMemory = performance as Performance & { readonly memory?: PerformanceMemory };
  const heapBytes = performanceWithMemory.memory?.usedJSHeapSize;
  return typeof heapBytes === "number" && Number.isFinite(heapBytes) ? heapBytes : null;
}

export class RuntimeMeter {
  private firstPaintMs: number | null = null;
  private lastSelectionMs: number | null = null;
  private worstSelectionMs: number | null = null;
  private selectionCount = 0;

  constructor(
    private readonly now: Clock = () => performance.now(),
    private readonly heapReader: HeapReader = readBrowserHeap
  ) {}

  recordFirstPaint(startedAt: number): void {
    if (this.firstPaintMs === null) {
      this.firstPaintMs = Math.max(0, this.now() - startedAt);
    }
  }

  recordSelection(startedAt: number): void {
    const duration = Math.max(0, this.now() - startedAt);
    this.lastSelectionMs = duration;
    this.worstSelectionMs = Math.max(this.worstSelectionMs ?? 0, duration);
    this.selectionCount += 1;
  }

  snapshot(): D0RuntimeMetrics {
    return {
      firstPaintMs: this.firstPaintMs,
      lastSelectionMs: this.lastSelectionMs,
      worstSelectionMs: this.worstSelectionMs,
      selectionCount: this.selectionCount,
      heapBytes: this.heapReader()
    };
  }
}

export function formatMilliseconds(value: number | null): string {
  return value === null ? "pending" : `${value.toFixed(1)} ms`;
}

export function formatBytes(value: number | null): string {
  if (value === null) {
    return "unavailable";
  }
  return `${(value / (1024 * 1024)).toFixed(1)} MiB`;
}
